import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Grid,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  CardActions,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  CreateNewFolder as CreateFolderIcon,
  Upload as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ProjectFolder, ProjectDocument } from '../../types/api';
import { 
  createFolder, 
  getProjectFolders, 
  updateFolder, 
  deleteFolder,
  uploadDocumentToFolder,
  isImageFile,
  isPdfFile
} from '../../services/projectsAPI';
import PDFViewer from '../PDFViewer/PDFViewer';

interface AdvancedFileManagerProps {
  projectId: number;
  currentFolderId?: number;
  onFolderChange?: (folderId: number | null) => void;
  onDocumentSelect?: (document: ProjectDocument) => void;
}

interface BreadcrumbItem {
  id: number | null;
  name: string;
}

export const AdvancedFileManager: React.FC<AdvancedFileManagerProps> = ({
  projectId,
  currentFolderId,
  onFolderChange,
  onDocumentSelect,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  
  const [folders, setFolders] = useState<ProjectFolder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<ProjectFolder | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: null, name: 'Root' }
  ]);
  const [loading, setLoading] = useState(false);
  
  // Dialog States
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [editFolderDialog, setEditFolderDialog] = useState(false);
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<ProjectFolder | null>(null);
  
  // Preview Modal States
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<ProjectDocument | null>(null);
  
  // Form States
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderDescription, setEditFolderDescription] = useState('');

  // Lade Ordner beim Komponenten-Mount
  useEffect(() => {
    loadFolders();
  }, [projectId]);

  // Aktualisiere aktuellen Ordner wenn sich currentFolderId ändert
  useEffect(() => {
    if (currentFolderId) {
      const folder = findFolderById(folders, currentFolderId);
      setCurrentFolder(folder);
      updateBreadcrumbs(folder);
    } else {
      setCurrentFolder(null);
      setBreadcrumbs([{ id: null, name: 'Root' }]);
    }
  }, [currentFolderId, folders]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const response = await getProjectFolders(projectId);
      setFolders(response.folders);
    } catch (error) {
      console.error('Error loading folders:', error);
      enqueueSnackbar('Fehler beim Laden der Ordner', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const findFolderById = (folderList: ProjectFolder[], id: number): ProjectFolder | null => {
    for (const folder of folderList) {
      if (folder.id === id) return folder;
      if (folder.sub_folders.length > 0) {
        const found = findFolderById(folder.sub_folders, id);
        if (found) return found;
      }
    }
    return null;
  };

  const updateBreadcrumbs = (folder: ProjectFolder | null) => {
    if (!folder) {
      setBreadcrumbs([{ id: null, name: 'Root' }]);
      return;
    }

    const breadcrumbItems: BreadcrumbItem[] = [{ id: null, name: 'Root' }];
    let current = folder;
    
    while (current.parent_folder_id) {
      const parent = findFolderById(folders, current.parent_folder_id);
      if (parent) {
        breadcrumbItems.unshift({ id: parent.id, name: parent.name });
        current = parent;
      } else {
        break;
      }
    }
    
    breadcrumbItems.push({ id: folder.id, name: folder.name });
    setBreadcrumbs(breadcrumbItems);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      enqueueSnackbar('Bitte geben Sie einen Ordnernamen ein', { variant: 'error' });
      return;
    }

    try {
      const folderData = {
        name: newFolderName.trim(),
        description: newFolderDescription.trim() || undefined,
        parent_folder_id: currentFolderId || undefined,
        sort_order: 0,
      };

      await createFolder(projectId, folderData);
      await loadFolders();
      
      setCreateFolderDialog(false);
      setNewFolderName('');
      setNewFolderDescription('');
      
      enqueueSnackbar('Ordner erfolgreich erstellt', { variant: 'success' });
    } catch (error) {
      console.error('Error creating folder:', error);
      enqueueSnackbar('Fehler beim Erstellen des Ordners', { variant: 'error' });
    }
  };

  const handleEditFolder = async () => {
    if (!selectedFolder || !editFolderName.trim()) {
      enqueueSnackbar('Bitte geben Sie einen Ordnernamen ein', { variant: 'error' });
      return;
    }

    try {
      const folderData = {
        name: editFolderName.trim(),
        description: editFolderDescription.trim() || undefined,
      };

      await updateFolder(projectId, selectedFolder.id, folderData);
      await loadFolders();
      
      setEditFolderDialog(false);
      setSelectedFolder(null);
      setEditFolderName('');
      setEditFolderDescription('');
      
      enqueueSnackbar('Ordner erfolgreich aktualisiert', { variant: 'success' });
    } catch (error) {
      console.error('Error updating folder:', error);
      enqueueSnackbar('Fehler beim Aktualisieren des Ordners', { variant: 'error' });
    }
  };

  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;

    try {
      await deleteFolder(projectId, selectedFolder.id);
      await loadFolders();
      
      setDeleteFolderDialog(false);
      setSelectedFolder(null);
      
      // Wenn der gelöschte Ordner der aktuelle war, gehe zurück zum Root
      if (currentFolderId === selectedFolder.id) {
        onFolderChange?.(null);
      }
      
      enqueueSnackbar('Ordner erfolgreich gelöscht', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting folder:', error);
      enqueueSnackbar('Fehler beim Löschen des Ordners', { variant: 'error' });
    }
  };

  const handleFolderClick = (folder: ProjectFolder) => {
    setCurrentFolder(folder);
    updateBreadcrumbs(folder);
    onFolderChange?.(folder.id);
  };

  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (item.id === null) {
      setCurrentFolder(null);
      setBreadcrumbs([{ id: null, name: 'Root' }]);
      onFolderChange?.(null);
    } else {
      const folder = findFolderById(folders, item.id);
      if (folder) {
        setCurrentFolder(folder);
        updateBreadcrumbs(folder);
        onFolderChange?.(folder.id);
      }
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const targetFolderId = currentFolderId || null;
    if (!targetFolderId) {
      enqueueSnackbar('Bitte wählen Sie einen Ordner für den Upload aus', { variant: 'error' });
      return;
    }

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await uploadDocumentToFolder(projectId, targetFolderId, file);
      }
      
      enqueueSnackbar('Dateien erfolgreich hochgeladen', { variant: 'success' });
      // Lade Ordner neu um Dokumente zu aktualisieren
      await loadFolders();
    } catch (error) {
      console.error('Error uploading files:', error);
      enqueueSnackbar('Fehler beim Hochladen der Dateien', { variant: 'error' });
    }
  };

  const getFileIcon = (filename: string) => {
    if (isPdfFile(filename)) return <PdfIcon />;
    if (isImageFile(filename)) return <ImageIcon />;
    return <FileIcon />;
  };

  const renderFolderItem = (folder: ProjectFolder, level: number = 0) => (
    <ListItem
      key={folder.id}
      onClick={() => handleFolderClick(folder)}
      sx={{ pl: level * 2 + 2, cursor: 'pointer' }}
    >
      <ListItemIcon>
        {currentFolderId === folder.id ? <FolderOpenIcon color="primary" /> : <FolderIcon />}
      </ListItemIcon>
      <ListItemText
        primary={folder.name}
        secondary={folder.description || `${folder.document_count} Dokumente`}
      />
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedFolder(folder);
            setEditFolderDialog(true);
            setEditFolderName(folder.name);
            setEditFolderDescription(folder.description || '');
          }}
        >
          <EditIcon />
        </IconButton>
        {!folder.is_system_folder && (
          <IconButton
            edge="end"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFolder(folder);
              setDeleteFolderDialog(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );

  const renderFolderTree = (folderList: ProjectFolder[], level: number = 0) => {
    return folderList.map(folder => (
      <Box key={folder.id}>
        {renderFolderItem(folder, level)}
        {folder.sub_folders.length > 0 && (
          <List component="div" disablePadding>
            {renderFolderTree(folder.sub_folders, level + 1)}
          </List>
        )}
      </Box>
    ));
  };

  const renderDocuments = () => {
    if (!currentFolder) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Dokumente in "{currentFolder.name}"
        </Typography>
        {currentFolder.documents && currentFolder.documents.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {currentFolder.documents.map((doc) => (
              <Card 
                key={doc.id}
                sx={{ 
                  '&:hover': { boxShadow: 3 }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    {getFileIcon(doc.filename)}
                    <Typography variant="body2" sx={{ ml: 1, flex: 1 }}>
                      {doc.original_filename}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(doc.created_at).toLocaleDateString('de-DE')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {(doc.file_size / 1024).toFixed(1)} KB
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewDocument(doc);
                    }}
                    title="Vorschau"
                    sx={{ color: 'primary.main' }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadDocument(doc);
                    }}
                    title="Download"
                    sx={{ color: 'success.main' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Keine Dokumente in diesem Ordner
            </Typography>
          </Paper>
        )}
      </Box>
    );
  };

  const handleDownloadDocument = (doc: ProjectDocument) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_API_URL || 'https://rooftopapp.hirnworx.com/api'}/projects/${projectId}/documents/${doc.id}/view?token=${token}`;
    
    // Erstelle einen temporären Link zum Download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = doc.original_filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    enqueueSnackbar(`Download gestartet: ${doc.original_filename}`, { variant: 'success' });
  };

  const handlePreviewDocument = (doc: ProjectDocument) => {
    if (isImageFile(doc.filename) || isPdfFile(doc.filename)) {
      // Öffne Modal für Vorschau
      setPreviewDocument(doc);
      setPreviewModalOpen(true);
    } else {
      // Für andere Dateitypen direkt Download starten
      handleDownloadDocument(doc);
    }
  };

  return (
    <Box>
      {/* Header mit Breadcrumbs */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Dateimanager</Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<CreateFolderIcon />}
                onClick={() => setCreateFolderDialog(true)}
                sx={{ mr: 1 }}
              >
                Neuer Ordner
              </Button>
              {currentFolderId && (
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  component="label"
                >
                  Dateien hochladen
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
              )}
            </Box>
          </Box>
          
          {/* Breadcrumbs */}
          <Box display="flex" alignItems="center" flexWrap="wrap">
            {breadcrumbs.map((item, index) => (
              <Box key={item.id} display="flex" alignItems="center">
                {index > 0 && <ArrowDownIcon sx={{ mx: 1, transform: 'rotate(-90deg)' }} />}
                <Chip
                  label={item.name}
                  clickable
                  onClick={() => handleBreadcrumbClick(item)}
                  variant={index === breadcrumbs.length - 1 ? "filled" : "outlined"}
                  color={index === breadcrumbs.length - 1 ? "primary" : "default"}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* Ordner-Navigation */}
        <Box sx={{ width: { xs: '100%', md: '33%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ordner
              </Typography>
              <List>
                {renderFolderTree(folders)}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Dokumente-Anzeige */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              {currentFolder ? (
                renderDocuments()
              ) : (
                <Box textAlign="center" py={4}>
                  <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Wählen Sie einen Ordner aus
                  </Typography>
                  <Typography color="text.secondary">
                    Klicken Sie auf einen Ordner in der linken Spalte, um dessen Inhalt anzuzeigen
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialog} onClose={() => setCreateFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Ordner erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Ordnername"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Beschreibung (optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newFolderDescription}
            onChange={(e) => setNewFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)}>Abbrechen</Button>
          <Button onClick={handleCreateFolder} variant="contained">Erstellen</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={editFolderDialog} onClose={() => setEditFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ordner bearbeiten</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Ordnername"
            fullWidth
            variant="outlined"
            value={editFolderName}
            onChange={(e) => setEditFolderName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Beschreibung (optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editFolderDescription}
            onChange={(e) => setEditFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFolderDialog(false)}>Abbrechen</Button>
          <Button onClick={handleEditFolder} variant="contained">Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Dialog */}
      <Dialog open={deleteFolderDialog} onClose={() => setDeleteFolderDialog(false)}>
        <DialogTitle>Ordner löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie den Ordner "{selectedFolder?.name}" löschen möchten?
            Alle enthaltenen Dokumente und Unterordner werden ebenfalls gelöscht.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialog(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteFolder} color="error" variant="contained">Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog 
        open={previewModalOpen} 
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          Vorschau: {previewDocument?.original_filename}
          <IconButton
            aria-label="close"
            onClick={() => setPreviewModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {previewDocument && (
            <Box sx={{ height: '100%', width: '100%' }}>
              {isImageFile(previewDocument.filename) ? (
                <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src={`${process.env.REACT_APP_API_URL || 'https://rooftopapp.hirnworx.com/api'}/projects/${projectId}/documents/${previewDocument.id}/view?token=${localStorage.getItem('token')}`}
                    alt={previewDocument.original_filename}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain' 
                    }}
                  />
                </Box>
              ) : isPdfFile(previewDocument.filename) ? (
                <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* PDF Toolbar */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    p: 1, 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      PDF-Vorschau
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleDownloadDocument(previewDocument)}
                      startIcon={<DownloadIcon />}
                    >
                      Herunterladen
                    </Button>
                  </Box>
                  
                  {/* PDF Content */}
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <object
                      data={`${process.env.REACT_APP_API_URL || 'https://rooftopapp.hirnworx.com/api'}/projects/${projectId}/documents/${previewDocument.id}/view?token=${localStorage.getItem('token')}`}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%', 
                        p: 3 
                      }}>
                        <Typography color="text.secondary" gutterBottom>
                          PDF kann nicht angezeigt werden
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => handleDownloadDocument(previewDocument)}
                          startIcon={<DownloadIcon />}
                        >
                          PDF herunterladen
                        </Button>
                      </Box>
                    </object>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">
                    Vorschau nicht verfügbar für diesen Dateityp
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewModalOpen(false)}>Schließen</Button>
          {previewDocument && (
            <Button 
              onClick={() => handleDownloadDocument(previewDocument)} 
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
